// File: frontend/src/components/PesaFiiBrand.js
//
// Single source of truth for the PesaFii sidebar branding.
//
// Renders the official PesaFii horizontal lockup (expanded) or the circular
// symbol (collapsed), picking the light/dark artwork from the app's *resolved*
// MUI theme mode — NOT prefers-color-scheme — because users toggle the theme
// manually (see ColorModeContext in App.js).
//
// The four SVGs live in frontend/public and are referenced by root-relative URL
// (no bundler import, no "public" in the path). Do not recolour/invert/filter
// them — each theme has its own dedicated asset that already contains the
// wordmark + "Access. Pay. Grow." tagline.

import React from "react";
import { Box, Tooltip } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Link } from "react-router-dom";

// Root-relative public URLs. These files must exist in frontend/public.
const PESA_FII_ASSETS = {
  light: {
    symbol: "/pesafii-symbol-black-green.svg",
    lockup: "/pesafii-sidebar-lockup-black-green.svg",
  },
  dark: {
    symbol: "/pesafii-symbol-white-green.svg",
    lockup: "/pesafii-sidebar-lockup-white-green.svg",
  },
};

/**
 * PesaFii brand mark, wrapped in a keyboard-focusable router link.
 *
 * @param {boolean} collapsed  Show the compact symbol instead of the full lockup.
 * @param {string}  linkTo     Route the brand navigates to (default: /dashboard).
 * @param {Function} onNavigate Optional click side-effect (e.g. close mobile drawer).
 */
export default function PesaFiiBrand({ collapsed = false, linkTo = "/dashboard", onNavigate, ariaLabel = "Go to PesaFii dashboard" }) {
  const theme  = useTheme();
  const isDark = theme.palette.mode === "dark";

  const assets = isDark ? PESA_FII_ASSETS.dark : PESA_FII_ASSETS.light;
  const src    = collapsed ? assets.symbol : assets.lockup;

  const image = (
    <Box
      component="img"
      src={src}
      alt=""                 // decorative — the parent link carries the accessible name
      draggable={false}
      sx={
        collapsed
          ? {
              // Compact symbol: 30–34px, aspect ratio preserved.
              width: 32,
              height: "auto",
              objectFit: "contain",
              display: "block",
              flexShrink: 0,
            }
          : {
              // Full lockup is 430×150 (~2.87:1). At 168px wide it is ~58.6px
              // tall, leaving a little breathing room inside the 62px area.
              // Cap the width, keep the ratio, never distort.
              width: "100%",
              maxWidth: 168,
              height: "auto",
              objectFit: "contain",
              display: "block",
            }
      }
    />
  );

  const brandLink = (
    <Box
      component={Link}
      to={linkTo}
      onClick={onNavigate}
      aria-label={ariaLabel}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: collapsed ? "center" : "flex-start",
        // Balanced branding-area height (58–68px) without excess.
        minHeight: 62,
        borderRadius: "10px",
        textDecoration: "none",
        outline: "none",
        transition: "opacity 0.15s ease",
        "&:hover": { opacity: 0.85 },
        // Subtle, theme-agnostic focus ring for keyboard users only.
        "&:focus-visible": {
          outline: "2px solid #4ade80",
          outlineOffset: "2px",
        },
      }}
    >
      {image}
    </Box>
  );

  // Only the collapsed symbol needs a tooltip to name the product; the expanded
  // lockup already shows the wordmark + tagline.
  return collapsed ? (
    <Tooltip title="PesaFii — Access. Pay. Grow." placement="right">
      {brandLink}
    </Tooltip>
  ) : (
    brandLink
  );
}
